// BarraTramos — barra segmentada del bloque de captura (multi-sku-por-hora,
// Lote 4). Muestra proporcionalmente cuánto de la celda cubre cada tramo
// agregado (color por SKU) y el hueco sin asignar en `warn-soft`. Solo se
// renderiza cuando hay 2+ tramos (con 1 tramo la UI es idéntica a hoy — ver
// L2CapturaPage.tsx, regla de oro del design).
//
// También vive acá `validarNuevoTramo` (pura, sin JSX): la validación en vivo
// del modal "Agregar tramo" — solape, fuera de bloque, hasta<=desde — para
// que L2CapturaPage.tsx no la duplique y sea unit-testeable sin renderizar.

import { finMinutosTramo, toMinutosTramo } from 'schemas';
import { duracionBloqueMin } from './shared';

// Paleta de colores por tramo — mismo patrón que AnalisisParosPage.tsx
// (CHART_COLORS): tokens semánticos existentes vía CSS var, NUNCA clases de
// Tailwind inventadas.
const TRAMO_COLORS = [
  'var(--accent)',
  'var(--info)',
  'var(--ok)',
  'var(--warn)',
  'var(--crit)',
  'var(--fg-secondary)',
];

export interface SegmentoTramo {
  hora_inicio_bloque: string;
  hora_fin_bloque: string;
  skuLabel: string;
}

/**
 * Valida en vivo (antes de "Agregar") que el tramo candidato `[desde,hasta)`:
 * 1. tiene `hasta` posterior a `desde` (o "00:00" fin de día),
 * 2. cae DENTRO de la celda `[celdaInicio, celdaFin)`,
 * 3. no se solapa con ningún tramo ya agregado.
 * Los huecos (desde > fin del último tramo cubierto) se PERMITEN acá — el
 * chip de minutos asignados es quien bloquea Guardar hasta partición
 * completa, no este modal (Req. UI, escenario "Chip bloquea Guardar").
 */
export function validarNuevoTramo(params: {
  desde: string;
  hasta: string;
  celdaInicio: string;
  celdaFin: string;
  tramosExistentes: { hora_inicio_bloque: string; hora_fin_bloque: string }[];
}): string | null {
  const { desde, hasta, celdaInicio, celdaFin, tramosExistentes } = params;
  if (!desde || !hasta) return 'Complete desde y hasta';

  const desdeMin = toMinutosTramo(desde);
  const hastaMin = finMinutosTramo(hasta);
  if (hastaMin <= desdeMin) return 'Hasta debe ser posterior a desde';

  const celdaIniMin = toMinutosTramo(celdaInicio);
  const celdaFinMin = finMinutosTramo(celdaFin);
  if (desdeMin < celdaIniMin || hastaMin > celdaFinMin) {
    return 'El tramo debe estar dentro del bloque';
  }

  const solapa = tramosExistentes.some((t) => {
    const tIni = toMinutosTramo(t.hora_inicio_bloque);
    const tFin = finMinutosTramo(t.hora_fin_bloque);
    return desdeMin < tFin && hastaMin > tIni;
  });
  if (solapa) return 'El tramo se solapa con uno existente';

  return null;
}

export function BarraTramos({
  celdaInicio,
  celdaFin,
  tramos,
}: {
  celdaInicio: string;
  celdaFin: string;
  tramos: SegmentoTramo[];
}) {
  const totalMin = duracionBloqueMin({
    hora_inicio_bloque: celdaInicio,
    hora_fin_bloque: celdaFin,
  });
  if (totalMin <= 0) return null;

  const cubiertoMin = tramos.reduce(
    (acc, t) =>
      acc +
      duracionBloqueMin({
        hora_inicio_bloque: t.hora_inicio_bloque,
        hora_fin_bloque: t.hora_fin_bloque,
      }),
    0,
  );
  const huecoMin = Math.max(0, totalMin - cubiertoMin);
  const huecoPct = (huecoMin / totalMin) * 100;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full border border-border"
        role="img"
        aria-label={`Barra de tramos del bloque ${celdaInicio}–${celdaFin}`}
      >
        {tramos.map((t, i) => {
          const dur = duracionBloqueMin({
            hora_inicio_bloque: t.hora_inicio_bloque,
            hora_fin_bloque: t.hora_fin_bloque,
          });
          const pct = (dur / totalMin) * 100;
          return (
            <div
              key={`${t.hora_inicio_bloque}-${t.hora_fin_bloque}-${i}`}
              className="h-full"
              style={{ width: `${pct}%`, backgroundColor: TRAMO_COLORS[i % TRAMO_COLORS.length] }}
              title={`${t.skuLabel} · ${t.hora_inicio_bloque}–${t.hora_fin_bloque}`}
            />
          );
        })}
        {huecoMin > 0 ? (
          <div
            className="h-full bg-warn-soft"
            style={{ width: `${huecoPct}%` }}
            title={`Sin asignar · ${Math.round(huecoMin)} min`}
          />
        ) : null}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-fg-tertiary">
        {tramos.map((t, i) => (
          <span key={`${t.hora_inicio_bloque}-leg-${i}`} className="inline-flex items-center gap-1">
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: TRAMO_COLORS[i % TRAMO_COLORS.length] }}
            />
            {t.skuLabel} · {t.hora_inicio_bloque}–{t.hora_fin_bloque}
          </span>
        ))}
      </div>
    </div>
  );
}
